import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Container, Stack } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { VSpacer } from './components/DesignSystem';
import { useFeatureFlags } from './hooks/useFeatureFlag';
import { FeatureFlag } from '@shared/enums';
import { initializeI18n } from './i18n';
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const AppWithI18nCheck = () => {
  const { isFlagOn, isFetched } = useFeatureFlags();
  const i18nInitializationStarted = useRef(false);
  const [i18nInitializationError, setI18nInitializationError] = useState<Error | null>(null);
  const [isI18nReady, setIsI18nReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAppI18n = async () => {
      if (!isFetched || i18nInitializationStarted.current || isI18nReady) {
        return;
      }

      i18nInitializationStarted.current = true;

      try {
        const canadianAdaptationsEnabled = isFlagOn(FeatureFlag.CanadianAdaptations);
        await initializeI18n(canadianAdaptationsEnabled);

        if (isMounted) {
          setIsI18nReady(true);
        }
      } catch (error: unknown) {
        if (isMounted) {
          setI18nInitializationError(
            error instanceof Error
              ? error
              : new Error('Failed to initialize i18n.'),
          );
        }
      }
    };

    void initializeAppI18n();

    return () => {
      isMounted = false;
    };
  }, [isFetched, isFlagOn, isI18nReady]);

  if (i18nInitializationError) {
    throw i18nInitializationError;
  }

  if (!isFetched || !isI18nReady) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  return <App />;
};

export const AppInitializer = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppWithI18nCheck />
    </QueryClientProvider>
  );
};
